import { useCallback, useEffect, useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';
import {
  DrawGeometryType,
  DrawingStyles,
  useDrawFromDialog,
  useDrawingContext,
} from '@ais-ui/react-openlayers';
import { GeoJson } from '@ais-ui/api-spec/shapes';
import { useViewMode, ViewMode } from '../components/ViewModeProvider';
import { useReplayContext } from '../modules/Replay/ReplayContext';

/**
 * A wrapper around useDrawFromDialog that handles ViewMode switching.
 *
 * This hook:
 * 1. Sets ViewMode.Drawing when drawing starts
 * 2. Returns to ViewMode.Normal when drawing ends
 * 3. Stops replay if active when drawing starts
 *
 * @param form - React Hook Form instance
 * @param setShowDialog - Function to show/hide the parent dialog
 * @param geometryField - Form field name for geometry (default: 'geometry')
 * @param drawingStyle - Optional drawing style preset
 */
export const useDrawWithViewMode = (
  form: UseFormReturn<any>,
  setShowDialog: (showDialog: boolean) => void,
  geometryField = 'geometry',
  drawingStyle?: DrawingStyles,
) => {
  const { showDrawPanel } = useDrawingContext();
  const { setViewMode, viewMode } = useViewMode();
  const { resetReplay } = useReplayContext();

  const {
    onStartDrawingFromDialog: baseOnStartDrawing,
    onAcceptDrawingFromDialog,
    onCancelDrawingFromDialog,
  } = useDrawFromDialog(form, setShowDialog, geometryField, drawingStyle);

  // Return to Normal mode when drawing ends
  useEffect(() => {
    if (!showDrawPanel && viewMode === ViewMode.Drawing) {
      setViewMode(ViewMode.Normal);
    }
  }, [showDrawPanel, viewMode, setViewMode]);

  // Keep the latest drawing logic in a ref so the returned callback can stay
  // referentially stable across renders. `useDrawFromDialog` returns a fresh
  // `baseOnStartDrawing` on every render; if that leaked into the callback's
  // identity, consumers that bake it into a memoised config (e.g. the detector
  // section rail) would rebuild that config each render and remount the active
  // pane — losing input focus and closing open selectors on every keystroke.
  const startDrawingImplRef = useRef<
    (
      geoType: DrawGeometryType,
      existingShape?: GeoJson | null,
      maxPoints?: number,
    ) => void
  >(() => undefined);
  startDrawingImplRef.current = (geoType, existingShape, maxPoints) => {
    // Stop replay if active
    if (viewMode === ViewMode.Replay) {
      resetReplay({ resetDate: true });
    }
    // Switch to Drawing mode
    setViewMode(ViewMode.Drawing);
    // Start the drawing
    baseOnStartDrawing(geoType, existingShape, maxPoints);
  };

  // Stable wrapper — always delegates to the latest implementation above.
  const onStartDrawingFromDialog = useCallback(
    (
      geoType: DrawGeometryType,
      existingShape?: GeoJson | null,
      maxPoints?: number,
    ) => startDrawingImplRef.current(geoType, existingShape, maxPoints),
    [],
  );

  return {
    onStartDrawingFromDialog,
    onAcceptDrawingFromDialog,
    onCancelDrawingFromDialog,
  };
};
